import type { ElementState } from '../renderer/ElementState';

/**
 * Represents a boundary point for element snapping
 */
export const PLAYHEAD_SNAP_BOUNDARY_ID = 'reference-playhead';

/**
 * Multiplier for snap warning range relative to snap threshold.
 * Warning range = snapThreshold * SNAP_WARNING_MULTIPLIER
 */
export const SNAP_WARNING_MULTIPLIER = 5;

export interface ElementBoundary {
  time: number;
  type: 'start' | 'end' | 'reference';
  elementId: string;
}

/**
 * Creates pre-calculated boundaries for efficient snapping during drag operations
 * @param elements - All timeline elements
 * @param activeElementIds - IDs of currently active/dragged elements to exclude from snapping
 * @param referenceTimes - Additional times with snapping significance (e.g., playhead, markers)
 * @param collapseThresholdSeconds - Merge boundaries that fall within this window
 * @returns Pre-calculated boundaries sorted by time
 */
export function createSnapBoundaries(
  elements: ElementState[],
  activeElementIds: string[] = [],
  referenceTimes: Array<number | { time: number; id?: string }> = [],
  collapseThresholdSeconds: number = 0,
): ElementBoundary[] {
  const boundaries: ElementBoundary[] = [];
  const elementList = elements ?? [];

  if (elementList.length > 0) {
    // Filter out active elements (the ones being dragged)
    const otherElements = elementList.filter(
      element => !activeElementIds.includes(element.source.id),
    );

    for (const element of otherElements) {
      // Add element start time
      boundaries.push({
        time: element.globalTime,
        type: 'start',
        elementId: element.source.id,
      });

      // Add element end time
      boundaries.push({
        time: element.globalTime + element.duration,
        type: 'end',
        elementId: element.source.id,
      });
    }
  }

  if (referenceTimes.length > 0) {
    referenceTimes.forEach((ref, index) => {
      const reference =
        typeof ref === 'number'
          ? { time: ref, id: `reference-${index}` }
          : {
              time: ref.time,
              id: ref.id ?? `reference-${index}`,
            };

      if (
        typeof reference.time !== 'number' ||
        !Number.isFinite(reference.time)
      ) {
        return;
      }

      boundaries.push({
        time: reference.time,
        type: 'reference',
        elementId: reference.id,
      });
    });
  }

  if (boundaries.length === 0) {
    return [];
  }

  // Sort boundaries by time for efficient binary search
  boundaries.sort((a, b) => a.time - b.time);

  return collapseCloseBoundaries(boundaries, collapseThresholdSeconds);
}

function collapseCloseBoundaries(
  boundaries: ElementBoundary[],
  collapseThresholdSeconds: number,
): ElementBoundary[] {
  if (
    collapseThresholdSeconds <= 0 ||
    !Number.isFinite(collapseThresholdSeconds) ||
    boundaries.length <= 1
  ) {
    return boundaries;
  }

  const collapsed: ElementBoundary[] = [];
  let group: ElementBoundary[] = [];

  const flushGroup = () => {
    if (group.length === 0) return;
    collapsed.push(selectBoundaryFromGroup(group));
    group = [];
  };

  boundaries.forEach(boundary => {
    if (group.length === 0) {
      group.push(boundary);
      return;
    }

    const previous = group[group.length - 1];
    if (Math.abs(boundary.time - previous.time) <= collapseThresholdSeconds) {
      group.push(boundary);
    } else {
      flushGroup();
      group.push(boundary);
    }
  });

  flushGroup();

  return collapsed;
}

function boundaryPriority(boundary: ElementBoundary): number {
  if (boundary.type === 'reference') {
    if (boundary.elementId === PLAYHEAD_SNAP_BOUNDARY_ID) {
      return 0;
    }
    return 3;
  }

  if (boundary.type === 'start') {
    return 1;
  }

  if (boundary.type === 'end') {
    return 2;
  }

  return 4;
}

function selectBoundaryFromGroup(group: ElementBoundary[]): ElementBoundary {
  return group.reduce((winner, candidate) => {
    const winnerPriority = boundaryPriority(winner);
    const candidatePriority = boundaryPriority(candidate);

    if (candidatePriority < winnerPriority) {
      return candidate;
    }

    if (candidatePriority > winnerPriority) {
      return winner;
    }

    if (candidate.time !== winner.time) {
      return candidate.time < winner.time ? candidate : winner;
    }

    return candidate.elementId < winner.elementId ? candidate : winner;
  });
}

function findClosestBoundary(
  time: number,
  boundaries: ElementBoundary[],
): ElementBoundary | null {
  if (boundaries.length === 0) {
    return null;
  }

  // Binary search to find insertion point
  let left = 0;
  let right = boundaries.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (boundaries[mid].time < time) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Check boundaries around insertion point for closest match within threshold
  const candidates: ElementBoundary[] = [];

  // Check boundary to the left
  if (left > 0) {
    const leftBoundary = boundaries[left - 1];
    candidates.push(leftBoundary);
  }

  // Check boundary to the right
  if (left < boundaries.length) {
    const rightBoundary = boundaries[left];
    candidates.push(rightBoundary);
  }

  if (candidates.length === 0) {
    return null;
  }

  // Return the closest candidate
  return candidates.reduce((closest, boundary) => {
    const distance = Math.abs(time - boundary.time);
    const closestDistance = Math.abs(time - closest.time);
    return distance < closestDistance ? boundary : closest;
  });
}

/**
 * Efficiently finds the closest boundary using binary search
 * @param time - The time to find closest boundary for
 * @param boundaries - Sorted array of boundaries
 * @param snapThreshold - Maximum distance to consider for snapping
 * @returns Closest boundary within threshold, or null if none found
 */
function findClosestBoundaryWithinSnapThreshold(
  time: number,
  boundaries: ElementBoundary[],
  snapThreshold: number,
): ElementBoundary | null {
  if (boundaries.length === 0) {
    return null;
  }

  const closestBoundary = findClosestBoundary(time, boundaries);

  if (!closestBoundary) {
    return null;
  }

  if (Math.abs(time - closestBoundary.time) <= snapThreshold) {
    return closestBoundary;
  }

  return null;
}

/**
 * Snaps time to the closest boundary using pre-calculated boundaries
 * @param time - The time to snap
 * @param snapBoundaries - Pre-calculated boundaries
 * @returns The snapped time, or original time if no nearby boundaries found
 */
export function snapTimeToBoundaries(
  time: number,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): number {
  const closestBoundary = findClosestBoundaryWithinSnapThreshold(
    time,
    snapBoundaries,
    snapThreshold,
  );

  return closestBoundary ? closestBoundary.time : time;
}

export interface SnapResult {
  snapTime: number | undefined;
  snapGuideTime: number | undefined;
  isWithinSnapRange: boolean;
  isWithinSnapWarningRange: boolean;
  boundary?: ElementBoundary | null;
}

export function getSnapResultForTime(
  time: number,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): SnapResult {
  const closestBoundary = findClosestBoundary(time, snapBoundaries);

  let snapTime;
  let isWithinSnapRange;
  let isWithinSnapWarningRange;

  if (!closestBoundary) {
    snapTime = undefined;
    isWithinSnapRange = false;
    isWithinSnapWarningRange = false;
    return {
      snapTime,
      snapGuideTime: undefined,
      isWithinSnapRange,
      isWithinSnapWarningRange,
      boundary: undefined,
    };
  } else {
    isWithinSnapRange = Math.abs(time - closestBoundary.time) <= snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time - closestBoundary.time) <= snapThreshold * 5;
    snapTime = isWithinSnapWarningRange ? closestBoundary.time : undefined;
  }

  return {
    snapTime,
    snapGuideTime: snapTime,
    isWithinSnapRange,
    isWithinSnapWarningRange,
    boundary: isWithinSnapWarningRange ? closestBoundary : undefined,
  };
}

export function getSnapResultForElement(
  time: number,
  element: ElementState,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): SnapResult {
  const closestHeadBoundary = findClosestBoundary(time, snapBoundaries);
  const closestTailBoundary = findClosestBoundary(
    time + element.duration,
    snapBoundaries,
  );

  const isHeadBoundaryCloser =
    closestHeadBoundary &&
    (!closestTailBoundary ||
      Math.abs(time - closestHeadBoundary.time) <=
        Math.abs(time + element.duration - closestTailBoundary.time));

  const isTailBoundaryCloser =
    closestTailBoundary &&
    (!closestHeadBoundary ||
      Math.abs(time + element.duration - closestTailBoundary.time) <=
        Math.abs(time - closestHeadBoundary.time));

  let snapTime: number | undefined;
  let snapGuideTime: number | undefined;
  let isWithinSnapRange;
  let isWithinSnapWarningRange;
  let boundary: ElementBoundary | undefined;

  if (isHeadBoundaryCloser) {
    isWithinSnapRange =
      Math.abs(time - closestHeadBoundary.time) <= snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time - closestHeadBoundary.time) <= snapThreshold * 5;
    snapTime = isWithinSnapWarningRange ? closestHeadBoundary.time : undefined;
    snapGuideTime = isWithinSnapWarningRange
      ? closestHeadBoundary.time
      : undefined;
    boundary = isWithinSnapWarningRange ? closestHeadBoundary : undefined;
  } else if (isTailBoundaryCloser) {
    isWithinSnapRange =
      Math.abs(time + element.duration - closestTailBoundary.time) <=
      snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time + element.duration - closestTailBoundary.time) <=
      snapThreshold * 5;
    snapTime = isWithinSnapWarningRange
      ? closestTailBoundary.time - element.duration
      : undefined;
    snapGuideTime = isWithinSnapWarningRange
      ? closestTailBoundary.time
      : undefined;
    boundary = isWithinSnapWarningRange ? closestTailBoundary : undefined;
  } else {
    snapTime = undefined;
    snapGuideTime = undefined;
    isWithinSnapRange = false;
    isWithinSnapWarningRange = false;
    boundary = undefined;
  }

  return {
    snapTime,
    snapGuideTime,
    isWithinSnapRange,
    isWithinSnapWarningRange,
    boundary,
  };
}

/**
 * Snaps element time to the closest boundary using pre-calculated boundaries.
 * This considers both the head and tail of the element, and their distances to the
 * nearest boundaries. If both the head and tail of the element are within the
 * snap threshold, the smaller gap is used to determine which boundary to snap to.
 * @param proposedElementStartTimecode - The desired new start time of the element.
 * @param element - The element to snap
 * @param snapBoundaries - Pre-calculated boundaries
 * @returns The snapped time, or original time if no nearby boundaries found
 */
export function snapElementTimeToBoundaries(
  proposedElementStartTimecode: number,
  element: ElementState,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): number {
  const closestHeadBoundary = findClosestBoundaryWithinSnapThreshold(
    proposedElementStartTimecode,
    snapBoundaries,
    snapThreshold,
  );

  const closestTailBoundary = findClosestBoundaryWithinSnapThreshold(
    proposedElementStartTimecode + element.duration,
    snapBoundaries,
    snapThreshold,
  );

  if (closestHeadBoundary && !closestTailBoundary) {
    return closestHeadBoundary.time;
  } else if (!closestHeadBoundary && closestTailBoundary) {
    return closestTailBoundary.time - element.duration;
  } else if (closestHeadBoundary && closestTailBoundary) {
    const gapAtElementHead = Math.abs(
      proposedElementStartTimecode - closestHeadBoundary.time,
    );
    const gapAtElementTail = Math.abs(
      closestTailBoundary.time -
        (proposedElementStartTimecode + element.duration),
    );
    if (gapAtElementHead < gapAtElementTail) {
      return closestHeadBoundary.time; // snap the head to its closest boundary
    }
    return closestTailBoundary.time - element.duration; // snap the tail to its closest boundary
  } else {
    return proposedElementStartTimecode;
  }
}

export function generateSnapThreshold(timelineScale: number = 50): number {
  if (timelineScale < 5) return 2;
  if (timelineScale < 10) return 1;
  return 10 / timelineScale;
}
